import { zodResolver } from '@hookform/resolvers/zod';
import { buttonVariants } from '@saay/shadcn/components/button';
import { type AdminSelf, type ApiResult, isApiError } from '@saay/api-client';
import {
  formatCount,
  formatLocalDate,
  formatTimestamp,
  isNationalCodeChecksumValid,
  MOBILE_E164_PATTERN,
  normalizeCodeInput,
  normalizeMobileField,
  parseSolarHijriInput,
  statusLabel,
  toLocalMobile,
  toPersianDigits,
} from '@saay/formatting';
import {
  AccordionPanel,
  applyFieldErrors,
  AssistChip,
  Banner,
  Button,
  Checkbox,
  CommandFeedback,
  DetailGrid,
  Dialog,
  EditableGrid,
  EmptyState,
  ErrorState,
  FactList,
  FieldErrorList,
  FileImportField,
  type FileImportPreview,
  LinearProgress,
  LoadMore,
  Ltr,
  NativeSelect,
  parseDelimitedImport,
  SheetSection,
  Skeleton,
  StatusChip,
  Table,
  TextArea,
  TextField,
  useToast,
  useWindowClass,
} from '../kit.js';
import { type CommandOutcome, useApiClient, useCommand } from '@saay/web-app';
import { useQueryClient } from '@tanstack/react-query';
import { type MutableRefObject, type ReactNode, useEffect, useMemo, useRef, useState } from 'react';
import { Controller, useFieldArray, useForm, type UseFormReturn } from 'react-hook-form';
import { Link, useLocation, useNavigate, useOutletContext, useParams } from 'react-router';
import { z } from 'zod';

import type {
  EnrollmentMemberInput,
  EnrollmentMembersAppend,
  EnrollmentMemberUpdate,
  EnrollmentProvisioning,
  EnrollmentProvisioningAccepted,
  EnrollmentRequest,
  EnrollmentRequestCreate,
  EnrollmentRequestMember,
  EnrollmentValidation,
  EnrollmentValidationPreview,
  Organization,
  OrganizationRole,
} from '../api.js';
import { TimeRemaining } from '../components/admin-feedback.js';
import { AdminSolarDateField } from '../components/admin-date-field.js';
import { CommandDialog } from '../components/command-dialog.js';
import { FilterPill, FilterPillBar } from '../components/filter-pills.js';
import { useSubmitBlocked } from '../components/form.js';
import { QueryState, usePageTitle } from '../components/guard.js';
import { GeographyFields } from '../components/pickers.js';
import { ReasonDialog } from '../components/reason-dialog.js';
import { onSheetOpenChange, useCloseSheet } from '../components/route-sheet.js';
import { creditProgramTargetingPath } from '../nav.js';
import { canUse } from '../permissions.js';
import {
  enrollmentPath,
  type EnrollmentMemberFilters,
  listStatus,
  organizationPath,
  setupKeys,
  useActiveOrganizationRoles,
  useEnrollmentMembers,
  useEnrollmentRequest,
  useOrganization,
} from '../queries.js';
import {
  enrollmentValidationErrorLabel,
  formatLocalMobileDisplay,
  formatProgramCodeRevision,
  formatRevisionLabel,
} from '../display.js';
import { tehranToday } from '../validation.js';
import type { OrganizationsOutletContext } from './organizations.js';

/**
 * Organization enrollment (AC §18.8.1 O10–O18; FTD §9.5): the request sheet with its member rows,
 * edited in the browser (typed or read from a CSV export of the organization's letter), then the
 * server's validation preview and the provisioning with the single-use preview token. Provisioning
 * runs as a job: the sheet shows the request `PROVISIONING` and re-reads it until it ends.
 */

/** Rows per create or append call (AC §18.8.1: 1..2000). */
const MAX_ROWS = 2000;

// ---------------------------------------------------------------------------------------------
// Member rows (shared by create and append)

const rowSchema = z.object({
  birthDate: z
    .string()
    .min(1, 'تاریخ تولد را وارد کنید.')
    .refine((value) => value < tehranToday(), 'تاریخ تولد باید در گذشته باشد.'),
  firstNameFa: z.string().trim().min(1, 'نام را وارد کنید.').max(100, 'حداکثر ۱۰۰ کاراکتر.'),
  guarantorCodes: z.string(),
  lastNameFa: z
    .string()
    .trim()
    .min(1, 'نام خانوادگی را وارد کنید.')
    .max(100, 'حداکثر ۱۰۰ کاراکتر.'),
  localTitleFa: z.string().trim().max(100, 'حداکثر ۱۰۰ کاراکتر.'),
  mobileE164: z
    .string()
    .transform(normalizeMobileField)
    .refine((value) => MOBILE_E164_PATTERN.test(value), 'شماره موبایل معتبر نیست.'),
  nationalCode: z
    .string()
    .transform(normalizeCodeInput)
    .refine((value) => /^\d{10}$/.test(value), 'کد ملی ۱۰ رقم است.')
    .refine(isNationalCodeChecksumValid, 'رقم کنترل کد ملی درست نیست.'),
  organizationRoleId: z.string().min(1, 'نقش را انتخاب کنید.'),
});
type RowInput = z.input<typeof rowSchema>;

const EMPTY_ROW: RowInput = {
  birthDate: '',
  firstNameFa: '',
  guarantorCodes: '',
  lastNameFa: '',
  localTitleFa: '',
  mobileE164: '',
  nationalCode: '',
  organizationRoleId: '',
};

/** A row nobody has typed in yet: not an error before the first entry (MD3 rule 2, W4). */
function isBlankRow(row: RowInput): boolean {
  return Object.values(row).every((value) => typeof value !== 'string' || value.trim() === '');
}

/** Duplicates inside one submission are refused by the server too (`400 DUPLICATE`). */
function rowsSchema(min: number) {
  return z
    .array(rowSchema)
    .min(min, 'دست‌کم یک ردیف لازم است.')
    .max(MAX_ROWS, `حداکثر ${toPersianDigits(MAX_ROWS)} ردیف در هر ارسال.`)
    .superRefine((rows, context) => {
      const seen = new Map<string, number>();
      rows.forEach((row, index) => {
        for (const field of ['nationalCode', 'mobileE164'] as const) {
          const key = `${field}:${row[field]}`;
          if (seen.has(key)) {
            context.addIssue({
              code: 'custom',
              message: `تکراری با ردیف ${toPersianDigits((seen.get(key) ?? 0) + 1)}.`,
              path: [index, field],
            });
          } else {
            seen.set(key, index);
          }
        }
      });
    });
}

function toMemberInput(row: z.output<typeof rowSchema>, exempt: boolean): EnrollmentMemberInput {
  const codes = row.guarantorCodes
    .split(/[\s,،]+/)
    .map((code) => code.trim())
    .filter((code) => code !== '');
  return {
    birthDate: row.birthDate,
    firstNameFa: row.firstNameFa,
    lastNameFa: row.lastNameFa,
    mobileE164: row.mobileE164,
    nationalCode: row.nationalCode,
    organizationRoleId: row.organizationRoleId,
    // `null` = the request default geography (AC §18.8.1).
    cityId: null,
    provinceId: null,
    ...(row.localTitleFa === '' ? {} : { localTitleFa: row.localTitleFa }),
    // A guarantor-exempt request sends no codes (F-7).
    ...(exempt ? {} : { guarantorCodes: codes }),
  };
}

const MEMBER_HEADER_ALIASES: Readonly<Record<string, keyof RowInput>> = {
  birth: 'birthDate',
  birthdate: 'birthDate',
  codes: 'guarantorCodes',
  first: 'firstNameFa',
  firstname: 'firstNameFa',
  guarantor: 'guarantorCodes',
  last: 'lastNameFa',
  lastname: 'lastNameFa',
  mobile: 'mobileE164',
  mobilee164: 'mobileE164',
  nationalcode: 'nationalCode',
  organizationroleid: 'organizationRoleId',
  role: 'organizationRoleId',
  title: 'localTitleFa',
  'کد معرف': 'guarantorCodes',
  'کد ملی': 'nationalCode',
  'کد نقش': 'organizationRoleId',
  نام: 'firstNameFa',
  'نام خانوادگی': 'lastNameFa',
  'تاریخ تولد': 'birthDate',
  'عنوان محلی': 'localTitleFa',
  موبایل: 'mobileE164',
  نقش: 'organizationRoleId',
};

function normalizeHeader(cell: string): string {
  return cell.trim().toLowerCase().replace(/\s+/g, '');
}

function isMemberHeaderRow(cells: readonly string[]): boolean {
  const joined = cells.join(' ').toLowerCase();
  return /mobile|موبایل|کد ملی|national/.test(joined);
}

function rowFromCells(
  cells: readonly string[],
  byCode: ReadonlyMap<string, string>,
  columnMap?: ReadonlyMap<keyof RowInput, number>,
): RowInput {
  const read = (field: keyof RowInput, fallbackIndex: number): string => {
    const index = columnMap?.get(field) ?? fallbackIndex;
    return cells[index] ?? '';
  };
  const date = read('birthDate', 4);
  const iso = /^\d{4}-\d{2}-\d{2}$/.test(date) ? date : (parseSolarHijriInput(date) ?? '');
  const roleCode = read('organizationRoleId', 5);
  return {
    birthDate: iso,
    firstNameFa: read('firstNameFa', 2),
    guarantorCodes: read('guarantorCodes', 7),
    lastNameFa: read('lastNameFa', 3),
    localTitleFa: read('localTitleFa', 6),
    mobileE164: read('mobileE164', 0),
    nationalCode: read('nationalCode', 1),
    organizationRoleId: byCode.get(roleCode.toUpperCase()) ?? '',
  };
}

/**
 * Reads a CSV or tab-separated export (one member per line). Header columns are detected by name when
 * present; otherwise columns follow the legacy order (mobile, national code, names, birth date, role
 * code, local title, guarantor codes).
 */
export function parseMembersText(text: string, roles: readonly OrganizationRole[]): RowInput[] {
  const byCode = new Map(roles.map((role) => [role.roleCode.toUpperCase(), role.id]));
  const table = parseDelimitedImport(text);
  if (table.length === 0) return [];
  let start = 0;
  let columnMap: Map<keyof RowInput, number> | undefined;
  if (isMemberHeaderRow(table[0]!)) {
    columnMap = new Map();
    table[0]!.forEach((header, index) => {
      const key =
        MEMBER_HEADER_ALIASES[normalizeHeader(header)] ?? MEMBER_HEADER_ALIASES[header.trim()];
      if (key !== undefined) columnMap!.set(key, index);
    });
    start = 1;
  }
  return table.slice(start).map((cells) => rowFromCells(cells, byCode, columnMap));
}

const ENROLLMENT_TEMPLATE_CSV =
  'موبایل,کد ملی,نام,نام خانوادگی,تاریخ تولد,کد نقش,عنوان محلی,کدهای معرف\n';

const ENROLLMENT_TEMPLATE_HREF = `data:text/csv;charset=utf-8,${encodeURIComponent(
  ENROLLMENT_TEMPLATE_CSV,
)}`;

export function buildMemberImportPreview(allRows: readonly RowInput[]): FileImportPreview | null {
  const rows = allRows.filter((row) => !isBlankRow(row));
  if (rows.length === 0) return null;
  let validCount = 0;
  let invalidCount = 0;
  const previewRows = rows.map((row, index) => {
    const parsed = rowSchema.safeParse(row);
    const valid = parsed.success;
    if (valid) validCount += 1;
    else invalidCount += 1;
    return {
      cells: [row.mobileE164, row.nationalCode, row.firstNameFa, row.lastNameFa, row.birthDate],
      key: String(index),
      status: (valid ? 'valid' : 'invalid') as 'invalid' | 'valid',
      statusLabel: valid ? 'معتبر' : 'خطا',
    };
  });
  return {
    columns: ['شماره موبایل', 'کد ملی', 'نام', 'نام خانوادگی', 'تاریخ تولد'],
    invalidCount,
    rows: previewRows,
    validCount,
  };
}

function invalidRowIndices(rows: readonly RowInput[]): number[] {
  const indices: number[] = [];
  rows.forEach((row, index) => {
    if (!isBlankRow(row) && !rowSchema.safeParse(row).success) {
      indices.push(index);
    }
  });
  return indices;
}

function focusEnrollmentRow(formRoot: HTMLElement | null, index: number): void {
  if (formRoot === null) {
    return;
  }
  const field = formRoot.querySelector<HTMLElement>(`[name="rows.${index}.mobileE164"]`);
  const row = field?.closest('tr');
  row?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
  field?.focus();
}

type MemberGridRow = RowInput & { readonly id: string; readonly index: number };

/** `sha256:` + hex of a file, the `evidenceDocumentHash` (Q-S3-13; the file never leaves the browser). */
async function hashFile(file: File): Promise<string> {
  const digest = await crypto.subtle.digest('SHA-256', await file.arrayBuffer());
  return `sha256:${[...new Uint8Array(digest)].map((byte) => byte.toString(16).padStart(2, '0')).join('')}`;
}

interface RowsForm {
  readonly rows: RowInput[];
}

/** The rows editor: editable grid (ADM-66) with optional legacy file paste in append flows. */
function RowsEditor<F extends RowsForm>({
  exempt,
  form,
  formRootRef,
  hideImport = false,
  jumpToError,
  onEvidenceHash,
}: {
  readonly exempt: boolean;
  readonly form: UseFormReturn<F, unknown, F>;
  readonly formRootRef?: MutableRefObject<HTMLFieldSetElement | null>;
  readonly hideImport?: boolean;
  readonly jumpToError?: { readonly onNext: () => void; readonly show: boolean };
  /** The attached file's hash, for the create form's `evidenceDocumentHash`. */
  readonly onEvidenceHash?: (hash: string) => void;
}) {
  const roles = useActiveOrganizationRoles();
  const typed = form as unknown as UseFormReturn<RowsForm, unknown, RowsForm>;
  const array = useFieldArray({ control: typed.control, name: 'rows' });
  const [pasted, setPasted] = useState('');
  const [importNote, setImportNote] = useState<string | undefined>(undefined);
  const roleOptions = (roles.data ?? []).map((role) => ({
    label: `${role.nameFa} · ${role.roleCode}`,
    value: role.id,
  }));
  const rowErrors = typed.formState.errors.rows;
  const values = typed.watch('rows');

  function load(text: string) {
    const parsed = parseMembersText(text, roles.data ?? []);
    const current = typed
      .getValues('rows')
      .filter((row) => row.nationalCode !== '' || row.mobileE164 !== '');
    typed.setValue('rows', [...current, ...parsed]);
    setImportNote(`${formatCount(parsed.length)} ردیف خوانده شد؛ پیش از ارسال بازبینی کنید.`);
  }

  const gridRows = useMemo((): MemberGridRow[] => {
    return array.fields.map((field, index) => ({
      id: field.id,
      index,
      ...(values[index] ?? EMPTY_ROW),
    }));
  }, [array.fields, values]);

  const columns = useMemo(() => {
    const defs = [
      {
        header: 'شماره موبایل',
        id: 'mobile',
        renderCell: (row: MemberGridRow) => (
          <TextField
            error={rowErrors?.[row.index]?.mobileE164?.message}
            inputMode="tel"
            label="شماره موبایل"
            ltr
            {...typed.register(`rows.${row.index}.mobileE164`)}
          />
        ),
      },
      {
        header: 'کد ملی',
        id: 'nationalCode',
        renderCell: (row: MemberGridRow) => (
          <TextField
            error={rowErrors?.[row.index]?.nationalCode?.message}
            inputMode="numeric"
            label="کد ملی"
            ltr
            {...typed.register(`rows.${row.index}.nationalCode`)}
          />
        ),
      },
      {
        header: 'نام',
        id: 'firstName',
        renderCell: (row: MemberGridRow) => (
          <TextField
            error={rowErrors?.[row.index]?.firstNameFa?.message}
            label="نام"
            {...typed.register(`rows.${row.index}.firstNameFa`)}
          />
        ),
      },
      {
        header: 'نام خانوادگی',
        id: 'lastName',
        renderCell: (row: MemberGridRow) => (
          <TextField
            error={rowErrors?.[row.index]?.lastNameFa?.message}
            label="نام خانوادگی"
            {...typed.register(`rows.${row.index}.lastNameFa`)}
          />
        ),
      },
      {
        header: 'تاریخ تولد',
        id: 'birthDate',
        renderCell: (row: MemberGridRow) => (
          <Controller
            control={typed.control}
            name={`rows.${row.index}.birthDate`}
            render={({ field: date, fieldState }) => (
              <AdminSolarDateField
                error={fieldState.error?.message}
                label="تاریخ تولد"
                name={date.name}
                onBlur={date.onBlur}
                onChange={date.onChange}
                value={date.value}
              />
            )}
          />
        ),
      },
      {
        header: 'نقش',
        id: 'role',
        renderCell: (row: MemberGridRow) => (
          <NativeSelect
            error={rowErrors?.[row.index]?.organizationRoleId?.message}
            label="نقش سراسری"
            options={roleOptions}
            placeholder="انتخاب"
            {...typed.register(`rows.${row.index}.organizationRoleId`)}
          />
        ),
      },
      {
        header: 'عنوان محلی',
        id: 'localTitle',
        renderCell: (row: MemberGridRow) => (
          <TextField
            error={rowErrors?.[row.index]?.localTitleFa?.message}
            label="عنوان محلی"
            {...typed.register(`rows.${row.index}.localTitleFa`)}
          />
        ),
      },
    ];
    if (!exempt) {
      defs.push({
        header: 'کدهای معرف',
        id: 'guarantor',
        renderCell: (row: MemberGridRow) => (
          <TextField
            label="کدهای معرف ضامن"
            ltr
            {...typed.register(`rows.${row.index}.guarantorCodes`)}
          />
        ),
      });
    }
    return defs;
  }, [exempt, roleOptions, rowErrors, typed]);

  return (
    <fieldset className="admin-fieldset" ref={formRootRef}>
      <legend>{`اعضا (${formatCount(array.fields.length)} ردیف)`}</legend>
      {hideImport ? null : (
        <details>
          <summary>خواندن از فایل یا متن</summary>
          <input
            accept=".csv,.tsv,.txt,.xlsx,text/csv,text/plain"
            aria-label="فایل فهرست اعضا"
            onChange={(event) => {
              const file = event.target.files?.[0];
              if (file === undefined) return;
              void file.text().then(load);
              if (onEvidenceHash !== undefined) void hashFile(file).then(onEvidenceHash);
            }}
            type="file"
          />
          <TextArea
            label="متن جدول"
            onChange={(event) => setPasted(event.target.value)}
            rows={4}
            value={pasted}
          />
          <Button
            disabled={pasted.trim() === '' || roles.status !== 'success'}
            onClick={() => {
              load(pasted);
              setPasted('');
            }}
            size="sm"
            variant="secondary"
          >
            افزودن از متن
          </Button>
          {importNote === undefined ? null : <p className="admin-muted">{importNote}</p>}
        </details>
      )}
      {typeof rowErrors?.message === 'string' ? (
        <span className="saay-field__error" role="alert">
          {rowErrors.message}
        </span>
      ) : null}
      {jumpToError?.show ? (
        <div className="admin-actions admin-actions--end">
          <Button onClick={jumpToError.onNext} size="sm" type="button" variant="secondary">
            پرش به خطای بعدی
          </Button>
        </div>
      ) : null}
      <EditableGrid
        addLabel="افزودن ردیف"
        caption={`جدول اعضا (${formatCount(array.fields.length)} ردیف)`}
        columns={columns}
        onAdd={() => {
          if (array.fields.length < MAX_ROWS) {
            array.append({ ...EMPTY_ROW });
          }
        }}
        onChange={(next) => {
          const { id: _id, index, ...data } = next;
          typed.setValue(`rows.${index}`, data, { shouldDirty: true, shouldValidate: true });
        }}
        onRemove={(id) => {
          const index = array.fields.findIndex((field) => field.id === id);
          if (index >= 0) {
            array.remove(index);
          }
        }}
        rows={gridRows}
      />
      {array.fields.length >= MAX_ROWS ? (
        <p className="admin-muted">
          بیش از ۲٬۰۰۰ ردیف را پس از ثبت درخواست با «افزودن ردیف‌ها» اضافه کنید.
        </p>
      ) : null}
    </fieldset>
  );
}

/** Server `fieldErrors` of a row body (`members[3].nationalCode`) → the editor's `rows.3.nationalCode`. */
function rowFieldPaths(count: number): string[] {
  const fields = [
    'mobileE164',
    'nationalCode',
    'firstNameFa',
    'lastNameFa',
    'birthDate',
    'organizationRoleId',
    'localTitleFa',
    'guarantorCodes',
  ];
  return [
    'rows',
    ...Array.from({ length: count }, (_, index) =>
      fields.map((field) => `rows.${index}.${field}`),
    ).flat(),
  ];
}

function mapRowErrors<F extends RowsForm>(
  error: unknown,
  form: UseFormReturn<F, unknown, F>,
  count: number,
  extra: readonly string[] = [],
) {
  if (!isApiError(error)) return [];
  // The server names `members[3].nationalCode`; match it as `members.3.nationalCode` and set the
  // editor's `rows.3.nationalCode`. (A copy of the error would lose its `ApiError` prototype, and
  // `applyFieldErrors` would then map and list nothing.)
  const paths = rowFieldPaths(count).map((path) => path.replace(/^rows/, 'members'));
  return applyFieldErrors(error, [...paths, ...extra], (name, value, options) =>
    (form as unknown as UseFormReturn<RowsForm, unknown, RowsForm>).setError(
      name.replace(/^members/, 'rows') as `rows.${number}.mobileE164`,
      value,
      options,
    ),
  );
}

/** The recovery line of a refused enrollment command, by error code only (AC §18.8.1). */
function enrollmentGuidance(error: unknown): ReactNode {
  if (!isApiError(error)) return undefined;
  const details = error.details ?? {};
  switch (error.code) {
    case 'ENROLLMENT_REQUEST_NOT_EDITABLE':
      return typeof details.status === 'string'
        ? `در وضعیت «${statusLabel('enrollmentRequestStatus', details.status)}» ردیف‌ها تغییر نمی‌کنند.`
        : 'این درخواست دیگر قابل ویرایش نیست.';
    case 'PREVIEW_EXPIRED':
      return 'مهلت پیش‌نمایش بررسی گذشته است؛ دوباره بررسی کنید.';
    case 'PREVIEW_STALE':
      return 'درخواست پس از پیش‌نمایش تغییر کرده است؛ دوباره بررسی کنید.';
    case 'ENROLLMENT_NO_VALID_ROWS':
      return 'هیچ ردیف معتبری نیست؛ ردیف‌های نامعتبر را اصلاح یا کنار بگذارید.';
    case 'CREDIT_PROGRAM_TARGET_NOT_ALLOWED':
      return 'برنامهٔ هدف باید به همین سازمان اختصاص داشته باشد.';
    case 'STATE_CONFLICT':
      return 'سازمان غیرفعال است و ثبت‌نام جدید نمی‌پذیرد.';
    case 'RESOURCE_VERSION_CONFLICT':
      return 'درخواست در این فاصله تغییر کرده است؛ اطلاعات به‌روز شد.';
    default:
      return undefined;
  }
}

// ---------------------------------------------------------------------------------------------
// O11 createEnrollmentRequest

const createSchema = z.object({
  defaultCityId: z.string(),
  defaultProvinceId: z.string(),
  evidenceDocumentHash: z.string(),
  evidenceReference: z
    .string()
    .trim()
    .min(1, 'شمارهٔ نامه یا مستند را وارد کنید.')
    .max(200, 'حداکثر ۲۰۰ کاراکتر.'),
  receivedDate: z
    .string()
    .min(1, 'تاریخ دریافت را وارد کنید.')
    .refine((value) => value <= tehranToday(), 'تاریخ دریافت نباید در آینده باشد.'),
  rows: rowsSchema(1),
  targetCreditProgramId: z.string(),
});
type CreateValues = z.input<typeof createSchema>;

function EnrollmentCreateForm({ organization }: { readonly organization: Organization }) {
  const toast = useToast();
  const navigate = useNavigate();
  const client = useApiClient();
  const queryClient = useQueryClient();
  const offline = useSubmitBlocked();
  const roles = useActiveOrganizationRoles();
  const formRootRef = useRef<HTMLFieldSetElement | null>(null);
  const errorFocusRef = useRef(0);
  const [importPreview, setImportPreview] = useState<FileImportPreview | null>(null);
  const [importIssue, setImportIssue] = useState<string | undefined>(undefined);
  const [unmapped, setUnmapped] = useState<ReturnType<typeof applyFieldErrors>>([]);
  const form = useForm<CreateValues, unknown, CreateValues>({
    defaultValues: {
      defaultCityId: organization.city.id,
      defaultProvinceId: organization.province.id,
      evidenceDocumentHash: '',
      evidenceReference: '',
      receivedDate: tehranToday(),
      rows: [{ ...EMPTY_ROW }],
      targetCreditProgramId: '',
    },
    resolver: zodResolver(createSchema as never) as never,
  });
  const command = useCommand<EnrollmentRequestCreate, EnrollmentRequest>(
    'organization.enrollment.create',
    (client, body, intent) =>
      client.post(organizationPath(organization.id, '/enrollment-requests'), { body, intent }),
    {
      onSuccess: async (result) => {
        await queryClient.invalidateQueries({ queryKey: setupKeys.organization(organization.id) });
        const validation = await client.postWithoutKey<EnrollmentValidationPreview>(
          enrollmentPath(result.data.id, '/validation'),
          { body: { expectedVersion: result.data.version } },
        );
        toast.success({
          confirmedBy: result,
          title: `درخواست با ${formatCount(result.data.memberCount)} ردیف ثبت و بررسی شد`,
        });
        void navigate(`/organizations/${organization.id}/enrollments/${result.data.id}`, {
          replace: true,
          state: { enrollmentPreview: validation.data },
        });
      },
    },
  );

  async function handle(outcome: CommandOutcome<EnrollmentRequest>) {
    if (outcome.status !== 'error') return;
    setUnmapped(
      mapRowErrors(outcome.error, form, form.getValues('rows').length, [
        'evidenceReference',
        'receivedDate',
        'targetCreditProgramId',
        'defaultProvinceId',
        'defaultCityId',
      ]),
    );
  }

  const dedicated = organization.dedicatedCreditPrograms.filter(
    (program) => program.status === 'ACTIVE',
  );
  const error = command.state.status === 'error' ? command.state.error : undefined;
  const line = error === undefined ? undefined : enrollmentGuidance(error);
  const watchedRows = form.watch('rows');
  const importSummary = useMemo(() => buildMemberImportPreview(watchedRows), [watchedRows]);
  const invalidRows = invalidRowIndices(watchedRows);

  function mergeImportedRows(parsed: RowInput[]) {
    const current = form
      .getValues('rows')
      .filter((row) => row.nationalCode !== '' || row.mobileE164 !== '');
    const next = [...current, ...parsed];
    form.setValue('rows', next.length === 0 ? [{ ...EMPTY_ROW }] : next);
    setImportPreview(buildMemberImportPreview(next));
  }

  async function ingestImportText(text: string) {
    if (roles.status !== 'success') return;
    mergeImportedRows(parseMembersText(text, roles.data ?? []));
  }

  async function ingestImportFile(file: File) {
    if (file.name.toLowerCase().endsWith('.xlsx')) {
      const header = new Uint8Array(await file.slice(0, 2).arrayBuffer());
      if (header[0] === 0x50 && header[1] === 0x4b) {
        setImportIssue('فایل اکسل را با «ذخیره به‌عنوان CSV» ذخیره کنید و همان را انتخاب کنید.');
        return;
      }
    }
    setImportIssue(undefined);
    await ingestImportText(await file.text());
    void hashFile(file).then((hash) => form.setValue('evidenceDocumentHash', hash));
  }

  return (
    <form
      className="admin-form"
      noValidate
      onSubmit={form.handleSubmit(async (values) => {
        setUnmapped([]);
        const parsed = createSchema.parse(values);
        await handle(
          await command.submit({
            evidenceReference: parsed.evidenceReference,
            members: parsed.rows.map((row) => toMemberInput(row, organization.guarantorExempt)),
            receivedDate: parsed.receivedDate,
            ...(parsed.evidenceDocumentHash === ''
              ? {}
              : { evidenceDocumentHash: parsed.evidenceDocumentHash }),
            ...(parsed.targetCreditProgramId === ''
              ? {}
              : { targetCreditProgramId: parsed.targetCreditProgramId }),
            ...(parsed.defaultProvinceId === ''
              ? {}
              : {
                  defaultCityId: parsed.defaultCityId,
                  defaultProvinceId: parsed.defaultProvinceId,
                }),
          }),
        );
      })}
    >
      <FileImportField
        accept=".csv,.tsv,.txt,.xlsx,text/csv,text/plain"
        label="فایل اعضا را اینجا رها کنید"
        onFile={(file) => void ingestImportFile(file)}
        onPaste={(text) => void ingestImportText(text)}
        preview={importPreview ?? importSummary}
        templateHref={ENROLLMENT_TEMPLATE_HREF}
      />
      {importIssue === undefined ? null : <Banner tone="warning">{importIssue}</Banner>}
      {importSummary === null ? null : (
        <div className="admin-actions admin-actions--spread">
          <p className="admin-enrollment-summary">
            {`${formatCount(importSummary.rows.length)} ردیف · ${formatCount(importSummary.invalidCount)} خطا`}
          </p>
          {invalidRows.length === 0 ? null : (
            <Button
              onClick={() => {
                const index = invalidRows[errorFocusRef.current % invalidRows.length]!;
                errorFocusRef.current += 1;
                focusEnrollmentRow(formRootRef.current, index);
              }}
              size="sm"
              type="button"
              variant="secondary"
            >
              پرش به خطای بعدی
            </Button>
          )}
        </div>
      )}
      <fieldset className="admin-fieldset">
        <legend>مستند سازمان</legend>
        <div className="admin-grid-2">
          <TextField
            error={form.formState.errors.evidenceReference?.message}
            hint="شمارهٔ نامه یا مستند رسمی سازمان"
            label="مستند ثبت‌نام"
            {...form.register('evidenceReference')}
          />
          <Controller
            control={form.control}
            name="receivedDate"
            render={({ field, fieldState }) => (
              <AdminSolarDateField
                error={fieldState.error?.message}
                label="تاریخ دریافت"
                name={field.name}
                onBlur={field.onBlur}
                onChange={field.onChange}
                value={field.value}
              />
            )}
          />
        </div>
        {form.watch('evidenceDocumentHash') === '' ? null : (
          <p className="admin-muted">
            {'اثر انگشت فایل پیوست: '}
            <bdi dir="ltr">{`${form.watch('evidenceDocumentHash').slice(0, 23)}…`}</bdi>
          </p>
        )}
        <NativeSelect
          hint="فقط برنامه‌های فعال اختصاصی همین سازمان؛ اعتبار پس از ثبت‌نام با هدف‌گذاری داده می‌شود."
          label="برنامهٔ اعتبار هدف (اختیاری)"
          options={dedicated.map((program) => ({
            label: `${program.nameFa} · ${formatProgramCodeRevision(program.programCode, program.revision)}`,
            value: program.id,
          }))}
          placeholder="بدون برنامهٔ هدف"
          {...form.register('targetCreditProgramId')}
        />
        <Controller
          control={form.control}
          name="defaultProvinceId"
          render={({ field: province }) => (
            <Controller
              control={form.control}
              name="defaultCityId"
              render={({ field: city }) => (
                <GeographyFields
                  cityId={city.value}
                  idPrefix="enrollment-default"
                  onCityChange={city.onChange}
                  onProvinceChange={province.onChange}
                  provinceId={province.value}
                />
              )}
            />
          )}
        />
      </fieldset>
      <RowsEditor
        exempt={organization.guarantorExempt}
        form={form as unknown as UseFormReturn<RowsForm, unknown, RowsForm>}
        formRootRef={formRootRef}
        hideImport
      />
      <FieldErrorList errors={unmapped} />
      <CommandFeedback
        onCheckStatus={() => void command.checkStatus().then(handle)}
        state={command.state}
      />
      {line === undefined ? null : <Banner tone="warning">{line}</Banner>}
      <div className="admin-actions admin-actions--end">
        <Button
          disabled={offline.blocked}
          loading={command.state.status === 'pending'}
          type="submit"
        >
          ثبت و بررسی
        </Button>
      </div>
    </form>
  );
}

/** `/organizations/:organizationId/enrollments/new`. */
export function EnrollmentCreateSheet() {
  const { self } = useOutletContext<OrganizationsOutletContext>();
  const { organizationId = '' } = useParams();
  const organization = useOrganization(organizationId);
  const close = useCloseSheet(`/organizations/${organizationId}`);
  usePageTitle('ثبت‌نام گروهی جدید');
  const allowed = canUse(self, 'createEnrollmentRequest');
  return (
    <Dialog
      eyebrow={organization.data?.displayNameFa ?? 'سازمان'}
      onOpenChange={onSheetOpenChange(close)}
      open
      size="wide"
      title="ثبت‌نام گروهی اعضا"
    >
      <QueryState
        error={organization.error}
        onRetry={() => void organization.refetch()}
        skeleton={<Skeleton label="در حال بارگذاری سازمان…" lines={6} />}
        status={organization.status}
      >
        {organization.data === undefined ? null : !allowed ? (
          <Banner tone="warning">ثبت‌نام گروهی فقط برای مدیر ارشد و مدیر عملیات ممکن است.</Banner>
        ) : organization.data.status !== 'ACTIVE' ? (
          <Banner tone="warning">سازمان غیرفعال است و ثبت‌نام جدید نمی‌پذیرد.</Banner>
        ) : (
          <EnrollmentCreateForm organization={organization.data} />
        )}
      </QueryState>
    </Dialog>
  );
}

// ---------------------------------------------------------------------------------------------
// The request sheet: O12 detail, O13 rows, O14 append, O15 row edit, O16–O18

function useRefreshEnrollment(request: EnrollmentRequest) {
  const queryClient = useQueryClient();
  return async (): Promise<void> => {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: setupKeys.enrollment(request.id) }),
      queryClient.invalidateQueries({
        queryKey: [...setupKeys.enrollment(request.id), 'members'],
      }),
      queryClient.invalidateQueries({ queryKey: setupKeys.organization(request.organization.id) }),
    ]);
  };
}

const appendSchema = z.object({ rows: rowsSchema(1) });
type AppendValues = z.input<typeof appendSchema>;

function AppendDialog({
  onOpenChange,
  request,
}: {
  readonly onOpenChange: (open: boolean) => void;
  readonly request: EnrollmentRequest;
}) {
  const toast = useToast();
  const refresh = useRefreshEnrollment(request);
  return (
    <CommandDialog<AppendValues, EnrollmentMembersAppend, EnrollmentRequest>
      defaultValues={{ rows: [{ ...EMPTY_ROW }] }}
      description="افزودن ردیف درخواست را به پیش‌نویس برمی‌گرداند و پیش‌نمایش قبلی باطل می‌شود."
      eyebrow={request.organization.displayNameFa}
      fields={rowFieldPaths(MAX_ROWS)}
      guidance={enrollmentGuidance}
      name="organization.enrollment.append"
      onError={async (error, form) => {
        mapRowErrors(error, form, form.getValues('rows').length);
        if (error.code === 'RESOURCE_VERSION_CONFLICT') await refresh();
      }}
      onOpenChange={onOpenChange}
      onSuccess={async (result) => {
        await refresh();
        toast.success({
          confirmedBy: result,
          title: `درخواست حالا ${formatCount(result.data.memberCount)} ردیف دارد`,
        });
        onOpenChange(false);
      }}
      schema={appendSchema as never}
      send={(client, body, intent) =>
        client.post(enrollmentPath(request.id, '/members'), { body, intent })
      }
      size="wide"
      submitLabel="افزودن ردیف‌ها"
      title="افزودن ردیف به درخواست"
      toBody={(values) => {
        const parsed = appendSchema.parse(values);
        return {
          expectedVersion: request.version,
          members: parsed.rows.map((row) => toMemberInput(row, request.guarantorExempt)),
        };
      }}
    >
      {(form) => (
        <RowsEditor
          exempt={request.guarantorExempt}
          form={form as unknown as UseFormReturn<RowsForm, unknown, RowsForm>}
        />
      )}
    </CommandDialog>
  );
}

const rowEditSchema = rowSchema.extend({
  excluded: z.boolean(),
  exclusionReason: z.string().trim().max(500, 'حداکثر ۵۰۰ کاراکتر.'),
});
type RowEditValues = z.input<typeof rowEditSchema>;

const ROW_EDIT_FIELDS = [
  'mobileE164',
  'nationalCode',
  'firstNameFa',
  'lastNameFa',
  'birthDate',
  'organizationRoleId',
  'localTitleFa',
  'exclusionReason',
] as const;

function RowEditMemberErrors({
  form,
  member,
}: {
  readonly form: UseFormReturn<RowEditValues, unknown, RowEditValues>;
  readonly member: EnrollmentRequestMember;
}) {
  useEffect(() => {
    for (const error of member.errors) {
      if (!ROW_EDIT_FIELDS.includes(error.field as (typeof ROW_EDIT_FIELDS)[number])) {
        continue;
      }
      form.setError(error.field as (typeof ROW_EDIT_FIELDS)[number], {
        message: error.messageFa,
        type: 'server',
      });
    }
    const first = member.errors[0];
    if (
      first !== undefined &&
      ROW_EDIT_FIELDS.includes(first.field as (typeof ROW_EDIT_FIELDS)[number])
    ) {
      const field = document.querySelector<HTMLElement>(`[name="${first.field}"]`);
      field?.focus();
    }
  }, [form, member]);
  return null;
}

function RowEditDialog({
  member,
  onOpenChange,
  request,
}: {
  readonly member: EnrollmentRequestMember;
  readonly onOpenChange: (open: boolean) => void;
  readonly request: EnrollmentRequest;
}) {
  const toast = useToast();
  const refresh = useRefreshEnrollment(request);
  const roles = useActiveOrganizationRoles();
  return (
    <CommandDialog<RowEditValues, EnrollmentMemberUpdate, EnrollmentRequestMember>
      defaultValues={{
        birthDate: member.birthDate,
        excluded: member.rowStatus === 'EXCLUDED',
        exclusionReason: member.exclusionReason ?? '',
        firstNameFa: member.firstNameFa,
        guarantorCodes: '',
        lastNameFa: member.lastNameFa,
        localTitleFa: member.localTitleFa ?? '',
        mobileE164: toLocalMobile(member.mobileE164),
        nationalCode: member.nationalCode,
        organizationRoleId: member.organizationRole.id,
      }}
      description="ویرایش ردیف، درخواست را به پیش‌نویس برمی‌گرداند تا دوباره بررسی شود."
      eyebrow={`ردیف ${toPersianDigits(member.rowNumber)}`}
      fields={[
        'mobileE164',
        'nationalCode',
        'firstNameFa',
        'lastNameFa',
        'birthDate',
        'organizationRoleId',
        'localTitleFa',
        // `excluded` has no message of its own on the checkbox: its errors are listed under the form.
        'exclusionReason',
      ]}
      guidance={enrollmentGuidance}
      name="organization.enrollment.member.update"
      onError={async (error) => {
        if (error.code === 'RESOURCE_VERSION_CONFLICT') await refresh();
      }}
      onOpenChange={onOpenChange}
      onSuccess={async (result) => {
        await refresh();
        toast.success({
          confirmedBy: result,
          title:
            result.data.rowStatus === 'EXCLUDED'
              ? `ردیف ${toPersianDigits(result.data.rowNumber)} کنار گذاشته شد`
              : `ردیف ${toPersianDigits(result.data.rowNumber)} به‌روز شد`,
        });
        onOpenChange(false);
      }}
      schema={
        rowEditSchema.superRefine((value, context) => {
          if (value.excluded && value.exclusionReason === '') {
            context.addIssue({
              code: 'custom',
              message: 'دلیل کنارگذاشتن را بنویسید.',
              path: ['exclusionReason'],
            });
          }
        }) as never
      }
      send={(client, body) =>
        client.patch(
          `/admin/organization-enrollment-request-members/${encodeURIComponent(member.id)}`,
          { body },
        )
      }
      size="wide"
      submitLabel="ثبت تغییر ردیف"
      title="ویرایش ردیف ثبت‌نام"
      toBody={(values) => {
        const parsed = rowEditSchema.parse(values);
        const changes: Partial<EnrollmentMemberUpdate> = {};
        if (parsed.mobileE164 !== member.mobileE164) changes.mobileE164 = parsed.mobileE164;
        if (parsed.nationalCode !== member.nationalCode) changes.nationalCode = parsed.nationalCode;
        if (parsed.firstNameFa !== member.firstNameFa) changes.firstNameFa = parsed.firstNameFa;
        if (parsed.lastNameFa !== member.lastNameFa) changes.lastNameFa = parsed.lastNameFa;
        if (parsed.birthDate !== member.birthDate) changes.birthDate = parsed.birthDate;
        if (parsed.organizationRoleId !== member.organizationRole.id) {
          changes.organizationRoleId = parsed.organizationRoleId;
        }
        if (parsed.localTitleFa !== (member.localTitleFa ?? '')) {
          changes.localTitleFa = parsed.localTitleFa === '' ? null : parsed.localTitleFa;
        }
        if (!request.guarantorExempt && parsed.guarantorCodes.trim() !== '') {
          changes.guarantorCodes = parsed.guarantorCodes
            .split(/[\s,،]+/)
            .filter((code) => code !== '');
        }
        const wasExcluded = member.rowStatus === 'EXCLUDED';
        if (parsed.excluded !== wasExcluded) {
          changes.excluded = parsed.excluded;
          changes.exclusionReason = parsed.excluded ? parsed.exclusionReason : null;
        }
        return Object.keys(changes).length === 0
          ? null
          : { ...changes, expectedVersion: member.version };
      }}
    >
      {(form) => (
        <>
          <RowEditMemberErrors form={form} member={member} />
          <div className="admin-grid-2">
            <TextField
              error={form.formState.errors.mobileE164?.message}
              inputMode="tel"
              label="شماره موبایل"
              ltr
              {...form.register('mobileE164')}
            />
            <TextField
              error={form.formState.errors.nationalCode?.message}
              inputMode="numeric"
              label="کد ملی"
              ltr
              {...form.register('nationalCode')}
            />
            <TextField
              error={form.formState.errors.firstNameFa?.message}
              label="نام"
              {...form.register('firstNameFa')}
            />
            <TextField
              error={form.formState.errors.lastNameFa?.message}
              label="نام خانوادگی"
              {...form.register('lastNameFa')}
            />
            <Controller
              control={form.control}
              name="birthDate"
              render={({ field, fieldState }) => (
                <AdminSolarDateField
                  error={fieldState.error?.message}
                  label="تاریخ تولد"
                  name={field.name}
                  onBlur={field.onBlur}
                  onChange={field.onChange}
                  value={field.value}
                />
              )}
            />
            <NativeSelect
              error={form.formState.errors.organizationRoleId?.message}
              label="نقش سراسری"
              options={(roles.data ?? []).map((role) => ({
                label: `${role.nameFa} · ${role.roleCode}`,
                value: role.id,
              }))}
              {...form.register('organizationRoleId')}
            />
            <TextField
              error={form.formState.errors.localTitleFa?.message}
              label="عنوان محلی"
              {...form.register('localTitleFa')}
            />
            {request.guarantorExempt ? null : (
              <TextField
                hint={`${formatCount(member.guarantorCodeCount)} کد ثبت شده؛ برای جایگزینی، کدهای جدید را وارد کنید.`}
                label="کدهای معرف ضامن"
                ltr
                {...form.register('guarantorCodes')}
              />
            )}
          </div>
          <Checkbox label="کنارگذاشتن این ردیف" {...form.register('excluded')} />
          {form.watch('excluded') ? (
            <TextArea
              error={form.formState.errors.exclusionReason?.message}
              label="دلیل کنارگذاشتن"
              maxLength={500}
              {...form.register('exclusionReason')}
            />
          ) : null}
        </>
      )}
    </CommandDialog>
  );
}

const EDITABLE: ReadonlySet<EnrollmentRequest['status']> = new Set(['DRAFT', 'VALIDATED']);

/** O16 then O17: the dry run, its preview (single use, 10 minutes), and the provisioning. */
function ValidationPanel({
  onPreviewGone,
  preview,
  request,
  self,
  setPreview,
}: {
  readonly onPreviewGone: () => void;
  readonly preview: EnrollmentValidationPreview | null;
  readonly request: EnrollmentRequest;
  readonly self: AdminSelf;
  readonly setPreview: (preview: EnrollmentValidationPreview | null) => void;
}) {
  const toast = useToast();
  const refresh = useRefreshEnrollment(request);
  const offline = useSubmitBlocked();
  const windowClass = useWindowClass('expanded');
  const validate = useCommand<EnrollmentValidation, EnrollmentValidationPreview>(
    'organization.enrollment.validate',
    // A preview takes no Idempotency-Key (AC §18.8 O16, §7.3); the intent only guards the double click.
    (client, body) => client.postWithoutKey(enrollmentPath(request.id, '/validation'), { body }),
    {
      onSuccess: async (result) => {
        setPreview(result.data);
        await refresh();
      },
    },
  );
  const provision = useCommand<EnrollmentProvisioning, EnrollmentProvisioningAccepted>(
    'organization.enrollment.provision',
    (client, body, intent) =>
      client.post(enrollmentPath(request.id, '/provisioning'), { body, intent }),
    {
      onSuccess: async (result) => {
        setPreview(null);
        await refresh();
        toast.success({
          confirmedBy: result,
          description: 'هر ردیف جداگانه ثبت می‌شود؛ وضعیت درخواست خودکار به‌روز می‌شود.',
          title: `ایجاد ${formatCount(result.data.validRowCount)} حساب شروع شد`,
        });
      },
    },
  );
  const canValidate = canUse(self, 'validateEnrollmentRequest') && EDITABLE.has(request.status);
  const canProvision = canUse(self, 'provisionEnrollmentRequest') && request.status === 'VALIDATED';
  if (!canValidate && !canProvision) {
    return null;
  }

  async function onProvisionOutcome(outcome: CommandOutcome<EnrollmentProvisioningAccepted>) {
    if (outcome.status === 'error' && isApiError(outcome.error)) {
      if (outcome.error.code === 'PREVIEW_EXPIRED' || outcome.error.code === 'PREVIEW_STALE') {
        onPreviewGone();
        await refresh();
      }
    }
  }

  const snapshot = preview?.snapshot;
  const provisionCount =
    snapshot === undefined ? 0 : snapshot.newBuyerCount + snapshot.existingBuyerCount;
  const provisioningDone =
    request.countsByRowStatus.PROVISIONED + request.countsByRowStatus.LINKED_EXISTING_BUYER;
  const provisioningTotal = request.countsByRowStatus.VALID;
  const provisionError =
    provision.state.status === 'error' ? enrollmentGuidance(provision.state.error) : undefined;
  const validateError =
    validate.state.status === 'error' ? enrollmentGuidance(validate.state.error) : undefined;
  return (
    <SheetSection id="enrollment-validation" title="بررسی و ایجاد حساب‌ها">
      {snapshot === undefined ? null : (
        <>
          <p className="admin-enrollment-summary">
            {`${formatCount(provisionCount)} آمادهٔ ایجاد · ${formatCount(snapshot.invalidCount)} نامعتبر · ${formatCount(snapshot.excludedCount)} کنارگذاشته`}
          </p>
          {windowClass === 'compact' ? null : (
            <DetailGrid
              items={[
                { key: 'new', label: 'خریدار جدید', value: formatCount(snapshot.newBuyerCount) },
                {
                  key: 'existing',
                  label: 'متصل به خریدار موجود',
                  value: formatCount(snapshot.existingBuyerCount),
                },
                { key: 'invalid', label: 'نامعتبر', value: formatCount(snapshot.invalidCount) },
                {
                  key: 'excluded',
                  label: 'کنارگذاشته',
                  value: formatCount(snapshot.excludedCount),
                },
              ]}
            />
          )}
          {snapshot.errorsByCode.length === 0 ? null : (
            <AccordionPanel
              items={[
                {
                  content: (
                    <FactList
                      items={snapshot.errorsByCode.map((row) => ({
                        key: row.code,
                        label: enrollmentValidationErrorLabel(row.code),
                        value: `${formatCount(row.count)} ردیف`,
                      }))}
                    />
                  ),
                  key: 'errors',
                  title: 'جزئیات خطاها',
                },
              ]}
              label="جزئیات خطاهای بررسی"
            />
          )}
          <TimeRemaining
            expiredLabel="مهلت پیش‌نمایش گذشت؛ دوباره بررسی کنید."
            expiresAt={preview!.expiresAt}
            label="مهلت پیش‌نمایش"
            onExpire={onPreviewGone}
          />
        </>
      )}
      {request.status === 'PROVISIONING' && provisioningTotal > 0 ? (
        <>
          <p className="admin-muted">
            {`${formatCount(provisioningDone)} از ${formatCount(provisioningTotal)}`}
          </p>
          <LinearProgress
            label="پیشرفت ایجاد حساب‌ها"
            value={(provisioningDone / provisioningTotal) * 100}
          />
        </>
      ) : null}
      <CommandFeedback
        onCheckStatus={() => void validate.checkStatus()}
        pendingLabel="در حال بررسی ردیف‌ها…"
        state={validate.state}
      />
      {validateError === undefined ? null : <Banner tone="warning">{validateError}</Banner>}
      <CommandFeedback
        onCheckStatus={() => void provision.checkStatus().then(onProvisionOutcome)}
        pendingLabel="در حال ارسال…"
        state={provision.state}
      />
      {provisionError === undefined ? null : <Banner tone="warning">{provisionError}</Banner>}
      {canValidate ? (
        <div className="admin-actions">
          <Button
            disabled={offline.blocked}
            loading={validate.state.status === 'pending'}
            onClick={() => {
              setPreview(null);
              validate.reset();
              void validate.submit({ expectedVersion: request.version });
            }}
            variant={snapshot === undefined ? 'primary' : 'secondary'}
          >
            {snapshot === undefined ? 'بررسی ردیف‌ها' : 'بررسی دوباره'}
          </Button>
        </div>
      ) : null}
      {canProvision && preview !== null ? (
        <div className="admin-enrollment-footer">
          <p className="admin-muted">
            {snapshot?.invalidCount === 0
              ? 'ردیف‌های معتبر ایجاد می‌شوند.'
              : `${formatCount(snapshot?.invalidCount ?? 0)} ردیف نامعتبر کنار می‌ماند`}
          </p>
          <Button
            disabled={offline.blocked || provisionCount === 0}
            fullWidth
            loading={provision.state.status === 'pending'}
            onClick={() =>
              void provision
                .submit({ previewToken: preview.previewToken, snapshotHash: preview.snapshotHash })
                .then(onProvisionOutcome)
            }
          >
            {`ایجاد ${formatCount(provisionCount)} حساب`}
          </Button>
        </div>
      ) : null}
    </SheetSection>
  );
}

type MemberRowFilter = 'ALL' | 'EXCLUDED' | 'INVALID' | 'VALID';

function memberListFilters(filter: MemberRowFilter): EnrollmentMemberFilters {
  if (filter === 'ALL') {
    return {};
  }
  return { rowStatus: filter };
}

function BulkExcludeDialog({
  members,
  onDone,
  onOpenChange,
}: {
  readonly members: readonly EnrollmentRequestMember[];
  readonly onDone: () => void | Promise<void>;
  readonly onOpenChange: (open: boolean) => void;
}) {
  const client = useApiClient();
  const offline = useSubmitBlocked();
  const toast = useToast();
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | undefined>(undefined);
  const form = useForm<{ reason: string }>({
    defaultValues: { reason: '' },
    resolver: zodResolver(
      z.object({
        reason: z
          .string()
          .trim()
          .min(1, 'دلیل کنارگذاشتن را بنویسید.')
          .max(500, 'حداکثر ۵۰۰ کاراکتر.'),
      }),
    ),
  });

  return (
    <Dialog
      description={`${formatCount(members.length)} ردیف با یک دلیل کنار گذاشته می‌شوند.`}
      footer={
        <div className="admin-actions admin-actions--end">
          <Button onClick={() => onOpenChange(false)} type="button" variant="ghost">
            انصراف
          </Button>
          <Button
            disabled={offline.blocked}
            loading={pending}
            onClick={() => {
              void form.handleSubmit(async (values) => {
                setPending(true);
                setError(undefined);
                try {
                  let last: ApiResult<EnrollmentRequestMember> | null = null;
                  for (const member of members) {
                    last = await client.patch<EnrollmentRequestMember>(
                      `/admin/organization-enrollment-request-members/${encodeURIComponent(member.id)}`,
                      {
                        body: {
                          excluded: true,
                          exclusionReason: values.reason,
                          expectedVersion: member.version,
                        },
                      },
                    );
                  }
                  if (last === null) {
                    setPending(false);
                    return;
                  }
                  setPending(false);
                  toast.success({
                    confirmedBy: last,
                    title: `${formatCount(members.length)} ردیف کنار گذاشته شد`,
                  });
                  onOpenChange(false);
                  await onDone();
                } catch (caught) {
                  setPending(false);
                  setError(isApiError(caught) ? caught.messageFa : 'کنارگذاشتن ردیف‌ها کامل نشد.');
                }
              })();
            }}
            type="button"
          >
            کنارگذاشتن انتخاب‌شده‌ها
          </Button>
        </div>
      }
      onOpenChange={onOpenChange}
      open
      title="کنارگذاشتن ردیف‌های انتخاب‌شده"
    >
      <form className="admin-form" noValidate>
        <TextArea
          error={form.formState.errors.reason?.message ?? error}
          label="دلیل کنارگذاشتن"
          maxLength={500}
          {...form.register('reason')}
        />
      </form>
    </Dialog>
  );
}

function MembersTable({
  request,
  self,
}: {
  readonly request: EnrollmentRequest;
  readonly self: AdminSelf;
}) {
  const [filter, setFilter] = useState<MemberRowFilter>('ALL');
  const readable = canUse(self, 'listEnrollmentRequestMembers');
  const members = useEnrollmentMembers(request.id, memberListFilters(filter), readable);
  const refresh = useRefreshEnrollment(request);
  useEffect(() => {
    if (
      request.status === 'PROVISIONING' ||
      request.status === 'COMPLETED' ||
      request.status === 'PARTIALLY_COMPLETED'
    ) {
      void members.refetch();
    }
  }, [members, request.status, request.version]);
  const [editing, setEditing] = useState<EnrollmentRequestMember | null>(null);
  const [selected, setSelected] = useState<ReadonlySet<string>>(() => new Set());
  const [bulkOpen, setBulkOpen] = useState(false);
  if (!readable) {
    // O13 rows carry national codes and mobiles; Marketing Admin reads only the request (§6.6).
    return null;
  }
  const rows = members.data?.pages.flatMap((page) => page.items) ?? [];
  const editable = EDITABLE.has(request.status) && canUse(self, 'updateEnrollmentRequestMember');
  const counts = request.countsByRowStatus;
  const filterChips: readonly {
    readonly count: number;
    readonly key: MemberRowFilter;
    readonly label: string;
  }[] = [
    { count: request.memberCount, key: 'ALL', label: 'همه' },
    { count: counts.VALID, key: 'VALID', label: 'معتبر' },
    { count: counts.INVALID, key: 'INVALID', label: 'نامعتبر' },
    { count: counts.EXCLUDED, key: 'EXCLUDED', label: 'کنار گذاشته' },
  ];
  const selectedMembers = rows.filter((row) => selected.has(row.id));

  function toggleRow(id: string): void {
    setSelected((current) => {
      const next = new Set(current);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  }

  function selectInvalidRows(): void {
    setSelected((current) => {
      const next = new Set(current);
      for (const row of rows) {
        if (row.rowStatus === 'INVALID') {
          next.add(row.id);
        }
      }
      return next;
    });
  }

  return (
    <SheetSection id="enrollment-members" title="ردیف‌های درخواست">
      <FilterPillBar
        activeFilterCount={filter === 'ALL' ? 0 : 1}
        label="فیلتر وضعیت ردیف"
        onClearAll={() => {
          setFilter('ALL');
          setSelected(new Set());
        }}
      >
        <FilterPill
          emptyLabel={`همه ${formatCount(request.memberCount)}`}
          label="وضعیت ردیف"
          onChange={(value) => {
            setFilter(value === '' ? 'ALL' : (value as MemberRowFilter));
            setSelected(new Set());
          }}
          options={filterChips
            .filter((chip) => chip.key !== 'ALL')
            .map((chip) => ({
              label: `${chip.label} ${formatCount(chip.count)}`,
              value: chip.key,
            }))}
          value={filter === 'ALL' ? '' : filter}
        />
      </FilterPillBar>
      {editable ? (
        <div className="admin-actions admin-actions--spread">
          <AssistChip onClick={selectInvalidRows}>انتخاب همهٔ نامعتبرها</AssistChip>
          {selected.size === 0 ? null : (
            <Button onClick={() => setBulkOpen(true)} size="sm" variant="secondary">
              {`کنارگذاشتن انتخاب‌شده‌ها (${formatCount(selected.size)})`}
            </Button>
          )}
        </div>
      ) : null}
      <QueryState
        empty={<EmptyState title="ردیفی با این وضعیت نیست" />}
        error={members.error}
        isEmpty={rows.length === 0}
        onRetry={() => void members.refetch()}
        skeleton={<Skeleton label="در حال بارگذاری ردیف‌ها…" lines={5} />}
        status={listStatus(members)}
      >
        <Table
          caption="ردیف‌های درخواست ثبت‌نام"
          columns={[
            ...(editable
              ? [
                  {
                    detail: false,
                    header: <span className="saay-visually-hidden">انتخاب</span>,
                    key: 'select',
                    pin: 'start' as const,
                    render: (row: EnrollmentRequestMember) => (
                      <Checkbox
                        checked={selected.has(row.id)}
                        label={
                          <span className="saay-visually-hidden">
                            {`انتخاب ردیف ${toPersianDigits(row.rowNumber)}`}
                          </span>
                        }
                        onChange={() => toggleRow(row.id)}
                      />
                    ),
                  },
                ]
              : []),
            {
              header: 'ردیف',
              key: 'row',
              numeric: true,
              ...(editable ? {} : { pin: 'start' as const }),
              render: (row) => toPersianDigits(row.rowNumber),
            },
            {
              header: 'نام',
              key: 'name',
              render: (row) =>
                row.buyerId === null ? (
                  `${row.firstNameFa} ${row.lastNameFa}`
                ) : (
                  <Link
                    to={`/buyers/${row.buyerId}`}
                  >{`${row.firstNameFa} ${row.lastNameFa}`}</Link>
                ),
            },
            {
              header: 'کد ملی',
              key: 'nationalCode',
              render: (row) => <bdi dir="ltr">{row.nationalCode}</bdi>,
            },
            {
              header: 'شماره موبایل',
              key: 'mobile',
              render: (row) => <Ltr>{formatLocalMobileDisplay(row.mobileE164)}</Ltr>,
            },
            { header: 'نقش', key: 'role', render: (row) => row.organizationRole.nameFa },
            {
              header: 'شهر',
              key: 'city',
              render: (row) => (row.city === null ? '—' : row.city.nameFa),
            },
            {
              header: 'وضعیت',
              key: 'status',
              render: (row) => (
                <StatusChip enumName="enrollmentMemberRowStatus" value={row.rowStatus} />
              ),
            },
            {
              header: 'خطاها',
              key: 'errors',
              render: (row) =>
                row.errors.length === 0 ? (
                  '—'
                ) : (
                  <ul className="admin-error-list">
                    {row.errors.map((error) => (
                      <li key={`${error.field}:${error.code}`}>{error.messageFa}</li>
                    ))}
                  </ul>
                ),
            },
            ...(filter === 'EXCLUDED'
              ? [
                  {
                    header: 'دلیل کنارگذاشتن',
                    key: 'exclusion',
                    render: (row: EnrollmentRequestMember) => row.exclusionReason ?? '—',
                  },
                ]
              : []),
            {
              detail: false,
              header: <span className="saay-visually-hidden">ویرایش</span>,
              key: 'edit',
              pin: 'end',
              render: (row) =>
                editable ? (
                  <Button onClick={() => setEditing(row)} size="sm" variant="ghost">
                    ویرایش
                  </Button>
                ) : null,
            },
          ]}
          rowKey={(row) => row.id}
          rows={rows}
        />
        {members.isFetchNextPageError ? (
          <ErrorState error={members.error} onRetry={() => void members.fetchNextPage()} />
        ) : null}
        <LoadMore
          hasMore={members.hasNextPage}
          loading={members.isFetchingNextPage}
          onLoadMore={() => void members.fetchNextPage()}
        />
      </QueryState>
      {editing === null ? null : (
        <RowEditDialog
          member={editing}
          onOpenChange={(next) => {
            if (!next) setEditing(null);
          }}
          request={request}
        />
      )}
      {bulkOpen && selectedMembers.length > 0 ? (
        <BulkExcludeDialog
          members={selectedMembers}
          onDone={async () => {
            setSelected(new Set());
            await refresh();
          }}
          onOpenChange={(next) => {
            if (!next) setBulkOpen(false);
          }}
        />
      ) : null}
    </SheetSection>
  );
}

type EnrollmentLocationState = {
  readonly enrollmentPreview?: EnrollmentValidationPreview;
};

function RequestView({
  request,
  self,
}: {
  readonly request: EnrollmentRequest;
  readonly self: AdminSelf;
}) {
  const location = useLocation();
  const initialPreview =
    (location.state as EnrollmentLocationState | null)?.enrollmentPreview ?? null;
  const [preview, setPreview] = useState<EnrollmentValidationPreview | null>(initialPreview);
  const [previewGone, setPreviewGone] = useState(false);
  const [appending, setAppending] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const refresh = useRefreshEnrollment(request);
  const toast = useToast();
  const counts = request.countsByRowStatus;
  const editable = EDITABLE.has(request.status);
  // A preview is valid only for the request version it was made for (AC §7.3).
  const livePreview =
    preview !== null && preview.snapshot.requestVersion === request.version ? preview : null;
  const grantCredit =
    request.suggestedTargeting !== null && canUse(self, 'previewCreditProgramTargeting');
  const completed = request.status === 'COMPLETED' || request.status === 'PARTIALLY_COMPLETED';
  return (
    <>
      <p className="admin-sheet-meta">
        <StatusChip enumName="enrollmentRequestStatus" value={request.status} />
      </p>
      {grantCredit && completed ? (
        <div className="admin-actions">
          <Link
            className={buttonVariants()}
            to={creditProgramTargetingPath(request.suggestedTargeting!.programId, {
              organizationId: request.suggestedTargeting!.target.organizationId,
              reason: 'اعتبار اعضای ثبت‌نام گروهی',
            })}
          >
            {`دادن اعتبار برنامه به ${formatCount(request.memberCount)} عضو`}
          </Link>
        </div>
      ) : null}
      {request.status === 'PROVISIONING' ? (
        <Banner tone="info">
          در حال ایجاد حساب‌ها؛ هر ردیف جداگانه ثبت می‌شود و این صفحه خودکار به‌روز می‌شود.
        </Banner>
      ) : null}
      {request.status === 'PARTIALLY_COMPLETED' ? (
        <Banner tone="warning">
          {`${formatCount(counts.FAILED)} ردیف ناموفق ماند؛ سایر ردیف‌ها ثبت شدند.`}
        </Banner>
      ) : null}
      {previewGone && livePreview === null && editable ? (
        <Banner tone="warning">پیش‌نمایش قبلی دیگر معتبر نیست؛ دوباره بررسی کنید.</Banner>
      ) : null}
      <DetailGrid
        items={[
          { key: 'rows', label: 'ردیف‌ها', value: formatCount(request.memberCount) },
          { key: 'valid', label: 'معتبر', value: formatCount(counts.VALID) },
          { key: 'invalid', label: 'نامعتبر', value: formatCount(counts.INVALID) },
          {
            key: 'done',
            label: 'ایجادشده / متصل',
            value: `${formatCount(counts.PROVISIONED)} / ${formatCount(counts.LINKED_EXISTING_BUYER)}`,
          },
        ]}
      />
      <SheetSection id="enrollment-facts" title="مستند و پیش‌فرض‌ها">
        <FactList
          items={[
            { key: 'evidence', label: 'مستند ثبت‌نام', value: request.evidenceReference },
            {
              key: 'hash',
              label: 'اثر انگشت فایل',
              value:
                request.evidenceDocumentHash === null ? (
                  '—'
                ) : (
                  <bdi dir="ltr">{`${request.evidenceDocumentHash.slice(0, 23)}…`}</bdi>
                ),
            },
            {
              key: 'received',
              label: 'تاریخ دریافت',
              value: formatLocalDate(request.receivedDate, 'long'),
            },
            {
              key: 'program',
              label: 'برنامهٔ هدف',
              value:
                request.targetCreditProgram === null
                  ? 'ندارد'
                  : `${request.targetCreditProgram.nameFa} · ${formatRevisionLabel(request.targetCreditProgram.revision)}`,
            },
            {
              key: 'geo',
              label: 'استان / شهر پیش‌فرض',
              value:
                request.defaultProvince === null
                  ? '—'
                  : `${request.defaultProvince.nameFa} / ${request.defaultCity?.nameFa ?? '—'}`,
            },
            { key: 'exempt', label: 'ضامن', value: request.guarantorExempt ? 'معاف' : 'لازم' },
            {
              key: 'by',
              label: 'ثبت‌کننده',
              value: `${request.recordedBy.displayNameFa ?? 'بدون نام'} · ${formatTimestamp(request.createdAt)}`,
            },
          ]}
        />
      </SheetSection>
      <ValidationPanel
        onPreviewGone={() => {
          setPreview(null);
          setPreviewGone(true);
        }}
        preview={livePreview}
        request={request}
        self={self}
        setPreview={(next) => {
          setPreview(next);
          setPreviewGone(false);
        }}
      />
      {grantCredit && completed ? (
        <Banner tone="success">اعتبار برنامه فقط با هدف‌گذاری به اعضا داده می‌شود.</Banner>
      ) : null}
      {editable &&
      (canUse(self, 'appendEnrollmentRequestMembers') ||
        canUse(self, 'cancelEnrollmentRequest')) ? (
        <div className="admin-actions">
          {canUse(self, 'appendEnrollmentRequestMembers') ? (
            <Button onClick={() => setAppending(true)} variant="secondary">
              افزودن ردیف‌ها
            </Button>
          ) : null}
          {canUse(self, 'cancelEnrollmentRequest') ? (
            <Button onClick={() => setCancelling(true)} variant="danger">
              لغو درخواست
            </Button>
          ) : null}
        </div>
      ) : null}
      <MembersTable request={request} self={self} />
      {appending ? (
        <AppendDialog
          onOpenChange={(next) => {
            if (!next) setAppending(false);
          }}
          request={request}
        />
      ) : null}
      {cancelling ? (
        <ReasonDialog<EnrollmentRequest>
          description="لغو قابل برگشت نیست و هیچ حسابی ساخته نمی‌شود."
          guidance={enrollmentGuidance}
          name="organization.enrollment.cancel"
          onDone={async (result) => {
            await refresh();
            toast.success({ confirmedBy: result, title: 'درخواست ثبت‌نام لغو شد' });
            setCancelling(false);
          }}
          onError={async (error) => {
            if (isApiError(error) && error.code === 'RESOURCE_VERSION_CONFLICT') await refresh();
          }}
          onOpenChange={(next) => {
            if (!next) setCancelling(false);
          }}
          reasonLabel="دلیل لغو"
          send={(client, reason, intent) =>
            client.post(enrollmentPath(request.id, '/cancellation'), {
              body: { expectedVersion: request.version, reason },
              intent,
            })
          }
          submitLabel="لغو درخواست"
          title="لغو درخواست ثبت‌نام"
          variant="danger"
        />
      ) : null}
    </>
  );
}

/** `/organizations/:organizationId/enrollments/:enrollmentRequestId`. */
export function EnrollmentRequestSheet() {
  const { self } = useOutletContext<OrganizationsOutletContext>();
  const { enrollmentRequestId = '', organizationId = '' } = useParams();
  const request = useEnrollmentRequest(enrollmentRequestId);
  const close = useCloseSheet(`/organizations/${organizationId}`);
  usePageTitle('درخواست ثبت‌نام گروهی');
  return (
    <Dialog
      eyebrow={request.data?.organization.displayNameFa ?? 'سازمان'}
      onOpenChange={onSheetOpenChange(close)}
      open
      size="wide"
      title="درخواست ثبت‌نام گروهی"
    >
      <QueryState
        error={request.error}
        onRetry={() => void request.refetch()}
        skeleton={<Skeleton label="در حال بارگذاری درخواست…" lines={8} />}
        status={request.status}
      >
        {request.data === undefined ? null : <RequestView request={request.data} self={self} />}
      </QueryState>
    </Dialog>
  );
}
